import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LANGUAGE } from "services/general";
import { HAS_PERMISSION } from "services/user";

function accessDeniedPath() {
  return `/${LANGUAGE}/access-denied`;
}

/**
 * Redirect to `/access-denied` when the user lacks create vs edit permission (route keyed by `id`).
 */
export function useRequireCreateOrEditPermission({
  id,
  createPermission,
  editPermission,
  enabled = true,
}) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!enabled) return;
    if (id && !HAS_PERMISSION(editPermission)) {
      navigate(accessDeniedPath());
    } else if (!id && !HAS_PERMISSION(createPermission)) {
      navigate(accessDeniedPath());
    }
  }, [navigate, id, createPermission, editPermission, enabled]);
}

/**
 * Redirect when the user lacks a single permission (e.g. create-only screens).
 */
export function useRequirePermission(permissionId) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!HAS_PERMISSION(permissionId)) {
      navigate(accessDeniedPath());
    }
  }, [navigate, permissionId]);
}

/**
 * List / index route guard — same behaviour as {@link useRequirePermission}.
 * Use with `*_INDEX` constants for domain list pages.
 */
export function useRequireIndexPermission(permissionId) {
  useRequirePermission(permissionId);
}

/**
 * Run the permission check only when `when` is true (e.g. active profile tab, visible panel).
 * Use on tab bodies or conditional sections so deep-links without access redirect once that UI is active.
 */
export function useRequirePermissionWhen(permissionId, when = true) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!when) return;
    if (!HAS_PERMISSION(permissionId)) {
      navigate(accessDeniedPath());
    }
  }, [navigate, permissionId, when]);
}

/**
 * Redirect unless the user has **at least one** of the permissions (OR).
 * Pass stable permission id arguments, e.g. `useRequireAnyPermission(A, B, C)`.
 */
export function useRequireAnyPermission(...permissionIds) {
  const navigate = useNavigate();
  useEffect(() => {
    if (!permissionIds.length) return;
    const allowed = permissionIds.some((p) => HAS_PERMISSION(p));
    if (!allowed) {
      navigate(accessDeniedPath());
    }
  }, [navigate, ...permissionIds]);
}

/** @typedef {"show" | "update" | "store"} PartyPermissionMode */

/**
 * Redirect based on investment party UI mode (view vs edit vs add).
 *
 * @param {PartyPermissionMode} mode
 * @param {{ show: number; update: number; store: number }} permissions
 */
export function useRequirePermissionByMode(mode, permissions) {
  const navigate = useNavigate();
  const { show, update, store } = permissions;
  useEffect(() => {
    const permission =
      mode === "show" ? show : mode === "update" ? update : store;
    if (!HAS_PERMISSION(permission)) {
      navigate(accessDeniedPath());
    }
  }, [navigate, mode, show, update, store]);
}

/**
 * Guard tabs navigation:
 *   - Redirects to access-denied if no tabs are visible to the user.
 *   - Redirects to access-denied if the user requests a tab they do not have permission for.
 *
 * @param {Array<{ name: string, permission?: number|string }>} tabs - All defined tabs
 * @param {string|null} activeTabName - Name of the currently active/requested tab
 */
export function useRequireTabsPermission(tabs, activeTabName) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!tabs || tabs.length === 0) return;

    // Filter tabs by permission
    const visibleTabs = tabs.filter(
      (tab) => !tab.permission || HAS_PERMISSION(tab.permission)
    );

    if (visibleTabs.length === 0) {
      navigate(accessDeniedPath());
      return;
    }

    if (activeTabName) {
      const targetTab = tabs.find((tab) => tab.name === activeTabName);
      if (targetTab) {
        const isAvailable = visibleTabs.some((tab) => tab.name === activeTabName);
        if (!isAvailable) {
          navigate(accessDeniedPath());
        }
      }
    }
  }, [navigate, tabs, activeTabName]);
}
